import React, { useState } from 'react';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import * as Types from 'pages/Pages/typings';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useSyncInfo from 'helpers/useSyncInfo';
import { OPEN_ROLE_PAGE_TYPE } from 'helpers/consts';

import DeletePageModal from 'pages/PageTypes/tabs/DeletePageModal';
import CreatePageModal from 'pages/PageTypes/components/CreatePageModal';
import NoPagesView from 'pages/PageTypes/components/NoPagesView';
import PagesTable from 'pages/PageTypes/tabs/PagesTable';
import PagesCards from 'pages/PageTypes/tabs/PagesCards';
import LastCsvUploadBanner from 'pages/PageTypes/tabs/LatestCsvUploadBanner';
import getPageType from 'helpers/getPageType';

interface Props {
  hasQuery: boolean;
  entityType: Types.EntityType;
  migrationInProgress?: boolean;
  pagesData: Types.EntitySearchResult;
  lastSyncStatusInfo?: any;
  onDeleteEntity: (entityId: string) => void;
  onReload: () => void;
}

function PagesTab({
  hasQuery,
  entityType,
  migrationInProgress,
  pagesData,
  lastSyncStatusInfo,
  onDeleteEntity,
  onReload,
}: Props) {
  const classes = useStyles();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const syncStatus = _get(lastSyncStatusInfo, 'syncStatus', {});
  const currentSyncRunning = !_isEmpty(syncStatus.currentSyncStatus);
  const { lastSyncStatus } = useSyncInfo(syncStatus);
  const forceShowTable = lastSyncStatus === 'error';
  const isIndependentPage = _isNil(_get(entityType, 'personClientFieldId', null));
  const pageTypeIsSyncManaged = _get(entityType, '_isSyncManaged', false);
  const pageType = getPageType(entityType);
  const deleteModalSubtitle = isIndependentPage
    ? 'Deleting this page will remove it from search result and remove all content on it. Type in page name to delete it.'
    : 'Deleting this page will remove it from profiles of users and remove all content on it. Type in page name to delete it.';
  const [selectEntity, setSelectEntity] = useState<Types.Entity | null>(null);
  const pages: Types.Entity[] = _get(pagesData, 'results', []);

  const handleDeletePage = () => {
    const entityId = _get(selectEntity, 'id');
    entityId && onDeleteEntity(entityId);
  };

  const handleClickDelete = () => {
    setIsDeleteModalOpen(true);
  };

  return (
    <>
      {(!!pages.length || hasQuery || forceShowTable) && !currentSyncRunning ? (
        <div className={classes.tableContainer}>
          <PagesCards pagesData={pagesData} />

          <LastCsvUploadBanner entityTypeId={entityType.id} />

          <PagesTable
            pageType={pageType}
            pagesData={pagesData}
            pageTypeIsSyncManaged={pageTypeIsSyncManaged}
            lastSyncStatus={syncStatus}
            onDelete={handleClickDelete}
            onSelectEntity={setSelectEntity}
            onSetCreateModalOpen={setIsCreateModalOpen}
          />
        </div>
      ) : (
        <NoPagesView
          migrationInProgress={migrationInProgress}
          pageTypeIsSyncManaged={pageTypeIsSyncManaged}
          isOpenRolePageType={pageType === OPEN_ROLE_PAGE_TYPE}
          entityTypeId={entityType.id}
          onClick={() =>
            migrationInProgress || currentSyncRunning ? onReload() : setIsCreateModalOpen(true)
          }
        />
      )}
      <CreatePageModal open={isCreateModalOpen} onSetOpen={setIsCreateModalOpen} />
      <DeletePageModal
        name={_get(selectEntity, 'name', '')}
        open={isDeleteModalOpen}
        subtitle={deleteModalSubtitle}
        onSetOpen={setIsDeleteModalOpen}
        onDelete={handleDeletePage}
      />
    </>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  tableContainer: {
    marginBottom: theme.spacing(5),
  },
}));

export default PagesTab;
